'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@src/utils/tailwind'

export const DesktopThumbnails = ({
  images,
  currentSlide,
  setCurrentSlide,
  height,
}: {
  images: Array<{ url: string; alt: string }>
  currentSlide: number
  setCurrentSlide: (slide: number) => void
  height?: number
}) => {
  const thumbnailContainerRef = useRef<HTMLDivElement>(null)

  const [thumbnailContainerScroll, setThumbnailContainerScroll] = useState({
    top: true,
    bottom: false,
  })

  useEffect(() => {
    const container = thumbnailContainerRef.current

    const handleScroll = () => {
      if (!container) return

      const isAtTop = container.scrollTop === 0
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight

      setThumbnailContainerScroll({
        top: isAtTop,
        bottom: isAtBottom,
      })
    }

    container?.addEventListener('scroll', handleScroll)

    return () => {
      container?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div
      ref={thumbnailContainerRef}
      className={cn(
        'flex shrink-0 flex-col gap-2 overflow-scroll transition-opacity scrollbar-none lg:mt-20',
        {
          hidden: images.length <= 1,
          // top and bottom fade
          '[mask-image:linear-gradient(to_top,rgba(255,255,255,0)_0%,rgba(0,0,0,1)_10%,rgba(0,0,0,1)_50%,rgba(0,0,0,1)_90%,rgba(255,255,255,0)_100%)]':
            !thumbnailContainerScroll.top && !thumbnailContainerScroll.bottom,
          // bottom fade
          '[mask-image:linear-gradient(to_top,rgba(255,255,255,0)_0%,rgba(0,0,0,1)_10%,rgba(0,0,0,1)_50%)]':
            !thumbnailContainerScroll.bottom && thumbnailContainerScroll.top,
          // top fade
          '[mask-image:linear-gradient(to_top,rgba(0,0,0,1)_90%,rgba(255,255,255,0)_100%)]':
            !thumbnailContainerScroll.top && thumbnailContainerScroll.bottom,
        }
      )}
      style={{ maxHeight: height ?? 'auto' }}
    >
      {images.map((image, index) => (
        <Image
          onClick={() => setCurrentSlide(index)}
          key={`${image.url}-${index}`}
          className={cn(
            'size-16 cursor-pointer rounded-lg border-2 border-transparent bg-bodi-gray-pearl',
            {
              'border-bodi-black': currentSlide === index,
            }
          )}
          src={image.url}
          alt={image.alt}
          height={100}
          width={100}
        />
      ))}
    </div>
  )
}
